import styled from '@emotion/styled'
import React, { FC, KeyboardEvent, RefObject, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'

import { useIsMobile } from '../../hooks/useBreakpoint'
import { ColumnFlex, VisuallyHidden } from '../Layout/Layout'

import SearchEndAdornment from './SearchEndAdornment'
import StyledTextField from './SearchStyledTextField'
import TextFieldHeading from './SearchTextFieldHeading'

const InputContainer = styled(ColumnFlex)`
  padding: 0 16px;
`

// eslint-disable-next-line @typescript-eslint/ban-types
interface Props {
  label: string
  placeholder?: string
  searchWord: string
  setSearchWord: (value: string) => void
  autoFocus?: boolean
  targetRef?: RefObject<HTMLHeadingElement>
}

const SearchAutocomplete: FC<Props> = ({
  label,
  placeholder,
  searchWord,
  setSearchWord,
  autoFocus,
  targetRef,
}): JSX.Element => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const textFieldId = useMemo(() => `searchTextField-${uuid()}`, [])

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (autoFocus) {
      if (document.activeElement !== inputRef.current) {
        inputRef.current?.focus()
      }
    }
  }, [autoFocus])

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && targetRef) {
      targetRef.current?.focus()
    }
  }

  const onAdormentClick = (value: string) => {
    setSearchWord(value)
    inputRef.current?.focus()
  }

  return (
    <>
      <InputContainer>
        <TextFieldHeading $size={isMobile ? 300 : 400} id={`${textFieldId}-heading`}>
          {label}
        </TextFieldHeading>
        <StyledTextField
          value={searchWord}
          inputRef={inputRef}
          onChange={(event) => setSearchWord(event.target.value)}
          id={textFieldId}
          size="medium"
          variant="outlined"
          placeholder={placeholder ?? t('component.search.searchTextField.placeholder')}
          InputProps={{
            'aria-labelledby': `${textFieldId}-heading`,
            'aria-describedby': `${textFieldId}-info`,
            endAdornment: <SearchEndAdornment value={searchWord} setValue={onAdormentClick} />,
            onKeyDown: onKeyDown,
          }}
        />
      </InputContainer>
      <VisuallyHidden id={`${textFieldId}-info`}>
        {t('component.search.searchTextField.ariaDescription')}
      </VisuallyHidden>
    </>
  )
}

export default SearchAutocomplete
